<script setup lang="ts">
import { computed, watchEffect } from 'vue';
import { normalizeTypography } from './config';
import { ensurePreviewFontCss } from './fontEngine';

/** `fallbackFamily`: the layer's own font, shown when the preset does not set one (style effects). */
const props = defineProps<{ config: Record<string, unknown>; fallbackFamily?: string }>();
const preset = computed(() => normalizeTypography(props.config));

watchEffect(() => {
  const font = preset.value.font;
  if (font) ensurePreviewFontCss(font.family, font.weight);
});

const fontStyle = computed(() => {
  const font = preset.value.font;
  if (font) return { fontFamily: `'${font.family}', system-ui`, fontWeight: font.weight };
  return props.fallbackFamily ? { fontFamily: `'${props.fallbackFamily}', system-ui` } : {};
});

const textStyle = computed(() => {
  const appearance = preset.value.appearance;
  const shadows: string[] = [];
  if (appearance.stroke.enabled && appearance.fill.type !== 'none') {
    const width = Math.max(1, Math.min(2, appearance.stroke.width));
    shadows.push(`${width}px 0 ${appearance.stroke.color}`, `-${width}px 0 ${appearance.stroke.color}`, `0 ${width}px ${appearance.stroke.color}`, `0 -${width}px ${appearance.stroke.color}`);
  }
  if (appearance.shadow.enabled) {
    for (const layer of appearance.shadow.layers) {
      shadows.push(`${(layer.x * 0.4).toFixed(1)}px ${(layer.y * 0.4).toFixed(1)}px ${(layer.blur * 0.4).toFixed(1)}px ${layer.color}`);
    }
  }
  if (appearance.extrude.enabled) shadows.push(`2px 2px ${appearance.extrude.color}`, `4px 4px ${appearance.extrude.color}`);
  const textShadow = shadows.join(',');
  if (appearance.fill.type === 'none') {
    return { color: 'transparent', WebkitTextStroke: `1.5px ${appearance.stroke.color}`, textShadow };
  }
  if (appearance.fill.type === 'linear-gradient' || appearance.fill.type === 'radial-gradient') {
    const backgroundImage = appearance.fill.type === 'linear-gradient'
      ? `linear-gradient(${appearance.fill.angle}deg, ${appearance.fill.colors.join(', ')})`
      : `radial-gradient(circle, ${appearance.fill.colors.join(', ')})`;
    return { backgroundImage, color: 'transparent', backgroundClip: 'text', WebkitBackgroundClip: 'text', textShadow };
  }
  return { color: appearance.fill.color, textShadow };
});
const transformStyle = computed(() => ({
  arc: 'rotate(-5deg)', circle: 'scale(.9) rotate(4deg)', wave: 'skewY(-6deg)', rise: 'rotate(-8deg)', flag: 'skewX(-10deg)', bulge: 'scale(1.12)',
}[preset.value.layout.mode]));
</script>

<template>
  <div class="vptw:relative vptw:flex vptw:h-14 vptw:items-center vptw:justify-center vptw:overflow-hidden vptw:rounded-lg vptw:bg-[radial-gradient(circle_at_center,#fff_0,#f1f5f9_100%)]">
    <span class="vptw:text-2xl vptw:font-black vptw:tracking-tight vptw:transition-transform" :style="[{ transform: transformStyle }, fontStyle, textStyle]">Aa</span>
    <span class="vptw:absolute vptw:bottom-1 vptw:right-1 vptw:rounded-full vptw:bg-white/90 vptw:px-1.5 vptw:py-0.5 vptw:text-[8px] vptw:font-semibold vptw:capitalize vptw:text-(--vs-ink-2)">{{ preset.layout.mode }}</span>
  </div>
</template>
